<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import {
  BarChart3,
  BookOpen,
  Home,
  Layers,
  Package,
  Phone,
  Receipt,
  TrendingUp,
  Users2,
  UserCog,
  Webhook,
  Wallet,
  KeyRound,
  Sparkles,
  X,
} from 'lucide-vue-next';
import BrandMark from './BrandMark.vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/uiStore';
import { useDeveloperMode } from '@/composables/useDeveloperMode';
import { cn } from '@/lib/cn';

const route = useRoute();
const dev = useDeveloperMode();
const auth = useAuthStore();
const ui = useUiStore();
const { t } = useI18n();
const isDesktop = useMediaQuery('(min-width: 1024px)');
const sidebarAriaHidden = computed(() => {
  if (isDesktop.value) return undefined;
  return ui.mobileNavOpen ? undefined : true;
});

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  devOnly?: boolean;
  exact?: boolean;
  adminOnly?: boolean;
}

interface NavGroup {
  /** Uppercase section label; omitted for the primary group. */
  label?: string;
  items: NavItem[];
}

// Grouped nav (Aria redesign) — the primary destinations sit unlabeled at the
// top; Admin and Developer surfaces fall under their own labelled sections so
// the rail reads as tiers rather than one long flat list.
const groups = computed<NavGroup[]>(() => {
  // Tenant SIP trunks (ADR-0025 cascade + ADR-0051) live under a
  // Settings-adjacent path; project-scoped SIP remains on Project Settings.
  const result: NavGroup[] = [
    {
      items: [
        { to: '/', label: t('sidebar.home'), icon: Home, exact: true },
        { to: '/calls', label: t('sidebar.calls'), icon: Phone },
        { to: '/reports', label: t('sidebar.reports'), icon: BarChart3 },
        { to: '/billing', label: t('sidebar.billing'), icon: Wallet },
        { to: '/settings/sip-trunks', label: t('sidebar.sipTrunks'), icon: Phone },
        { to: '/docs', label: t('sidebar.docs'), icon: BookOpen },
      ],
    },
  ];
  if (auth.isPlatformAdmin) {
    result.push({
      label: t('sidebar.groupAdmin'),
      items: [
        { to: '/admin/projects', label: t('sidebar.allProjects'), icon: Layers, adminOnly: true },
        {
          to: '/admin/industry-packs',
          label: t('sidebar.packEditor'),
          icon: Package,
          adminOnly: true,
        },
        {
          to: '/admin/billing/rate-cards',
          label: t('sidebar.adminRateCards'),
          icon: Receipt,
          adminOnly: true,
        },
        {
          to: '/admin/billing/tenants',
          label: t('sidebar.adminBillingTenants'),
          icon: Users2,
          adminOnly: true,
        },
        {
          to: '/admin/test-users',
          label: t('sidebar.adminTestUsers'),
          icon: UserCog,
          adminOnly: true,
        },
        {
          to: '/admin/economics',
          label: t('sidebar.adminEconomics'),
          icon: TrendingUp,
          adminOnly: true,
        },
      ],
    });
  }
  if (dev.enabled.value) {
    result.push({
      label: t('sidebar.groupDeveloper'),
      items: [
        { to: '/webhooks', label: t('sidebar.webhooks'), icon: Webhook, devOnly: true },
        { to: '/api-keys', label: t('sidebar.apiKeys'), icon: KeyRound, devOnly: true },
      ],
    });
  }
  return result;
});

function isActive(item: NavItem): boolean {
  if (item.exact) return route.path === item.to;
  return route.path.startsWith(item.to);
}

// Close the mobile drawer whenever the route changes — otherwise the
// drawer would stay open after the user taps a nav link.
watch(
  () => route.fullPath,
  () => {
    if (ui.mobileNavOpen) ui.closeMobileNav();
  },
);

function onNavClick(): void {
  if (ui.mobileNavOpen) ui.closeMobileNav();
}
</script>

<template>
  <!-- Mobile overlay -->
  <Transition
    enter-active-class="transition-opacity duration-200 ease-apple"
    leave-active-class="transition-opacity duration-150 ease-apple"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="ui.mobileNavOpen"
      class="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
      aria-hidden="true"
      @click="ui.closeMobileNav()"
    />
  </Transition>

  <aside
    :class="cn(
      'glass border-r border-border flex flex-col w-[260px] sm:w-[280px] lg:w-[240px] shrink-0 h-screen z-50',
      // Mobile: off-canvas drawer, slides in
      'fixed top-0 left-0 transform transition-transform duration-300 ease-apple',
      ui.mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
      // Desktop: sticky column, always visible
      'lg:sticky lg:translate-x-0 lg:z-30',
    )"
    :aria-hidden="sidebarAriaHidden"
  >
    <div class="flex items-center justify-between px-5 pt-5 pb-4">
      <BrandMark size="md" />
      <button
        type="button"
        class="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-bg hover:text-text transition-colors lg:hidden"
        :aria-label="t('topbar.close')"
        @click="ui.closeMobileNav()"
      >
        <X class="h-4 w-4" />
      </button>
    </div>

    <nav class="flex-1 px-3 py-2 overflow-y-auto">
      <div v-for="(group, gi) in groups" :key="gi" :class="gi > 0 ? 'mt-4' : ''">
        <div
          v-if="group.label"
          class="px-3 pt-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-text-subtle"
        >
          {{ group.label }}
        </div>
        <div class="space-y-0.5">
          <RouterLink
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            :class="cn(
              'flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-lg text-[14px] transition-colors duration-200 ease-apple',
              isActive(item)
                ? 'bg-accent-soft text-accent font-semibold'
                : 'text-text-muted hover:bg-bg-2 hover:text-text',
            )"
            @click="onNavClick"
          >
            <component :is="item.icon" class="h-4 w-4 shrink-0" />
            <span class="flex-1">{{ item.label }}</span>
            <span
              v-if="item.devOnly"
              class="text-[9px] font-semibold uppercase tracking-wider text-text-subtle bg-surface border border-border rounded px-1.5 py-0.5"
            >
              dev
            </span>
          </RouterLink>
        </div>
      </div>
    </nav>

    <div class="px-3 pb-4">
      <RouterLink
        to="/quickstart"
        class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium bg-accent-soft text-accent border border-accent-line hover:bg-accent/15 transition-colors"
        @click="onNavClick"
      >
        <Sparkles class="h-4 w-4" />
        <span>{{ t('sidebar.createAgent') }}</span>
      </RouterLink>
    </div>
  </aside>
</template>
